import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SearchOffersKeywordsWidget } from './SearchOffersKeywordsWidget';
import {
    IDropDownValue,
    IDropDownValueChildren,
    IDropDownValueOfferType,
    IDropDownValueOfferTypeChildren,
    IDropDownValueSectors,
    IGroupedOption,
    IOfferList,
    IOffersFilterParams,
    OfferTypeEnum,
} from '../../Models';
import { OfferService, ReferenceService } from '../../Services';
import { WidgetHelper } from '../WidgetHelper';
import { OfferSearchFilter, OfferList } from '../../Components';
import { localityDb } from '../../DexieDB';
import Modal from 'react-modal';
import { Paginator } from '../../Components/Paginator';
import {
    IReferenceDomaine,
    IReferenceSousDomaine,
} from '../../Models/Reference';
import { IReferenceSousContrat } from '../../Models/Reference';
import { IReferenceLocalities } from '../../Models/Reference';

interface ParamsNotFromFilter {
    localities?: IReferenceLocalities[];
}

interface ISearchOffersProps {
    connectionPageUrl: string;
    isAuthenticated: string;
    dashboardPageUrl: string;
    keywords: string;
    selectedDomains: string | null;
    selectedTypeOffers: string | null;
    selectedSectors: number | null;
    preselectDomain: string | null;
    useSessionStorage: string | null;
    publishedDateFilter: number | null;
    locationFilter: number | null;
    workTimeFilter: number | null;
    publishedLanguageFilter: number | null;
    offerTypes: OfferTypeEnum;
    language: string;
    offerDetailPageName: string;
    homepageUrl: string;
    isSjabloons: string;
    isHealthFilter: string;
    dictJobsSkillsKeywords: string;
    dictOffers: string;
    dictRefineResults: string;
    dictSearchBy: string;
    dictThe: string;
    dictContractType: string;
    dictPreselectActiris: string;
    dictNbSelectedDomain: string;
    dictSelectedDomain: string;
    dictTypeOfferStudent: string;
    dictTypeOfferStage: string;
    dictDomain: string;
    dictWorkFamily: string;
    dictWorkFamilies: string;
    dictSearch: string;
    dictLevel: string;
    dictOneLevel: string;
    dictOfferCreateDate: string;
    dictAllOffers: string;
    dictLast30Days: string;
    dictLast7Days: string;
    dictLocation: string;
    dictWorkTime: string;
    dictExperience: string;
    dictLanguage: string;
    dictAll: string;
    dictDay: string;
    dictWeek: string;
    dictMonth: string;
    dictBelgium: string;
    dictForeign: string;
    dictFullTime: string;
    dictPartialTime: string;
    dictWith: string;
    dictWithout: string;
    dictWithConvention: string;
    dictOfferSourceTitle: string;
    dictOfferSourceEmployment: string;
    dictOfferSourceStudent: string;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictSelectExplanation: string;
    dictSelectActirisDescription: string;
    dictOfferSave: string;
    dictOfferDelete: string;
    dictBasketOffers: string;
    dictSeeJobSeekerOffers: string;
    dictRemoveFilter: string;
    dictSector: string;
    dictIsCovidOnly: string;
    dictFilterShowOnly: string;
    dictCovidDescription: string;
    title: string;
    shortDescription: string;
    imageUrl: string;
    bgPos: string;
    isCovid: string;
    showCovidOffers: string;
    descriptionBeforeOffers: string;
    dictDistricts: string;
    dictMunicipalities: string;
    dictProvinces: string;
    dictOffer: string;
    dictRegions: string;
    dictLocalitiesDefaultMessage: string;
    dictLocations: string;
    dictAdvertTitle: string;
    dictAdvertDescription: string;
    dictAdvertLabel: string;
    dictNbSelectedDomains: string;
    dictTempHospitalOffers: string;
    dictKeywordTitleOnly: string;
    dictActirisOffers: string;
    dictVdabOffers: string;
    dictOthersOffers: string;
    dictHandicapOffers: string;
    dictRef: string;
    dictLocalities: string;
    dictToday: string;
    dictEdited: string;
    dictPublished: string;
    dictYesterday: string;
    dictDaysAgo: string;
    dictWeeksAgo: string;
    dictWeekAgo: string;
    dictMonthsAgo: string;
    dictMonthAgo: string;
    dictYearsAgo: string;
    dictYearAgo: string;
    sectorTitle: string;
    sectorDescription: string;
    jobTitle: string;
    jobDescription: string;
    dictLanguageInformation: string;
    dictLanguageMoreInformation: string;
    dictOnFocus: string;
    dictResultSearch: string;
    dictFor: string;
    dictZoneBelgium: string;
    alertUrl: string;
    alertTitle: string;
    alertDescription: string;
    alertEnabled: string;
    employerFilter: string;
}

type Localisation = 'Tout' | 'Belgique' | 'Etranger';
type KeywordSearchType = 'Titre' | 'Partout';
type QueryParams = Record<string, string | string[]>;

export const SearchOffersWidget: React.FC<ISearchOffersProps> = (props) => {
    const cancellationTokenRef = useRef<AbortController | null>(null);
    const isCovid = !!(props.isCovid && props.isCovid.toUpperCase() === 'TRUE');
    const regexNameLandingPageSeo = new RegExp('(\\{1})', 'g');
    const regexCountLandingPageSeo = new RegExp('(\\{2})', 'g');
    const [selectedDomains] = useState<string[]>([]);
    const [isSectorFilter] = useState<boolean>(false);

    const queryParamsToState = useCallback((): QueryParams => {
        const url = new URL(window.location.href.replace(/#/g, '%23'));
        const params = url.searchParams;

        const result: QueryParams = {};

        params.forEach((value, key) => {
            if (result[key] === undefined) {
                result[key] = value;
            } else if (Array.isArray(result[key])) {
                result[key].push(value);
            } else {
                result[key] = [result[key], value];
            }
        });
        return result;
    }, []);

    const findInQuery = useCallback(
        (queryParams: QueryParams, key: string): string | string[] | null => {
            const value = queryParams[key];
            return value ?? null;
        },
        []
    );

    const buildInitialFilterParams = (
        queryParams?: URLSearchParams
    ): IOffersFilterParams => {
        const rawLocalisation = queryParams?.get('localisation');
        const localisation: Localisation =
            rawLocalisation === 'Tout' ||
            rawLocalisation === 'Belgique' ||
            rawLocalisation === 'Etranger'
                ? rawLocalisation
                : 'Tout';

        const rawKeywordSearchType = queryParams?.get('keywordSearchType');
        const keywordSearchType: KeywordSearchType =
            rawKeywordSearchType === 'Titre' ||
            rawKeywordSearchType === 'Partout'
                ? rawKeywordSearchType
                : 'Partout';

        const rawDate = queryParams?.get('dateDerniereModification');
        const dateDerniereModification: Date | null = rawDate
            ? new Date(rawDate)
            : null;

        const keywordsFromUrl = queryParams?.get('keywords') ?? '';

        return {
            pageOption: {
                page: Number(queryParams?.get('page')) || 1,
                from: 0,
                pageSize: 10,
            },
            offreFilter: {
                texte: isNaN(Number(keywordsFromUrl)) ? keywordsFromUrl : '',
                regimesTravail:
                    queryParams?.get('regimesTravail')?.split(',') ?? [],
                dateDerniereModification,
                langue: queryParams?.get('langue') ?? null,
                codesPostal: [],
                codesContrat:
                    queryParams?.get('codesContrat')?.split(',') ?? [],
                domainesImt: queryParams?.get('domains')?.split(',') ?? [],
                secteursPanorama:
                    queryParams
                        ?.get('secteursPanorama')
                        ?.split(',')
                        .map((e) => +e) ?? [],
                references:
                    !isNaN(Number(keywordsFromUrl)) &&
                    keywordsFromUrl.trim() !== ''
                        ? [keywordsFromUrl]
                        : [],
                localisation,
                keywordSearchType,
                isOffreActiris: queryParams?.get('isOffreActiris') === 'true',
                isOffreVdabForem:
                    queryParams?.get('isOffreVdabForem') === 'true',
                isOfferPartner: queryParams?.get('isOfferPartner') === 'true',
                isOffreHandicap: queryParams?.get('isOffreHandicap') === 'true',
                employerFilter: [],
            },
        };
    };

    const [domains, setDomains] = useState<IDropDownValue[]>([]);
    const [offersType, setOffersType] = useState<IDropDownValueOfferType[]>([]);
    const [offersList, setOffersList] = useState<IOfferList | null>(null);
    const [sectors, setSectors] = useState<IDropDownValueSectors[]>([]);
    const [offersLoadedCount, setOffersLoadedCount] = useState<number>(0);
    const [queryParams, setQueryParams] = useState<any>(queryParamsToState());
    const [selectedLocalities, setSelectedLocalities] = useState<
        IReferenceLocalities[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [offersFilterParams, setOffersFilterParams] =
        useState<IOffersFilterParams>(() => {
            const queryParams = new URLSearchParams(window.location.search);
            return buildInitialFilterParams(queryParams);
        });

    const [hasMore] = useState<boolean>(true);
    const [isEmployerActirisOnly] = useState<boolean>(
        props.offerTypes === OfferTypeEnum.Actiris
    );
    const [isMobile] = useState<boolean>(window.innerWidth < 992);
    const [minHeight, setMinHeightState] = useState<string>('0px');
    const [alertEnabled] = useState<boolean>(props.alertEnabled !== 'False');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [currentSearchedWord, setCurrentSearchedWord] = useState<string>(
        () => new URLSearchParams(window.location.search).get('keywords') ?? ''
    );

    const getAllLocalities = useCallback(
        async (inputValue: string) => {
            if (!inputValue) return [];

            const data = await ReferenceService.getLocalities(inputValue);
            const options: IGroupedOption[] = [];

            if (data?.arrondissements?.length) {
                options.push({
                    label: props.dictDistricts,
                    code: 'arrondissements',
                    options: data.arrondissements.map((a) => ({
                        ...a,
                        value: `arr-${a.arrondissementId}`,
                        label: a.libelle,
                    })),
                });
            }

            if (data?.communes?.length) {
                options.push({
                    label: props.dictMunicipalities,
                    code: 'communes',
                    options: data.communes.map((c) => {
                        const cp = Array.isArray(c.codePostal)
                            ? c.codePostal[0]
                            : c.codePostal;
                        return {
                            ...c,
                            value: `com-${c.communeId}`,
                            label: `${c.libelle} (${cp})`,
                            codePostal: cp,
                        };
                    }),
                });
            }

            if (data?.provinces?.length) {
                options.push({
                    label: props.dictProvinces,
                    code: 'provinces',
                    options: data.provinces.map((p) => ({
                        ...p,
                        value: `prov-${p.provinceId}`,
                        label: p.libelle,
                    })),
                });
            }

            if (data.regions?.length) {
                options.push({
                    label: props.dictRegions,
                    code: 'regions',
                    options: data.regions.map((r) => ({
                        communeId: 0,
                        provinceId: undefined,
                        arrondissementId: 0,
                        codePostal: r.codePostal,
                        libelle: r.libelle,
                        label: r.libelle,
                        value: `reg-${r.libelle.replace(/\s+/g, '-')}`,
                    })),
                });
            }
            return options;
        },
        [
            props.dictDistricts,
            props.dictMunicipalities,
            props.dictProvinces,
            props.dictRegions,
        ]
    );

    const localitiesQueryTolocalitiresObj = useCallback(
        async (
            array: string[]
        ): Promise<{ localities: IReferenceLocalities[]; cps: number[] }> => {
            const localities: IReferenceLocalities[] = [];
            let cps: number[] = [];

            for (const locality of array) {
                const [label, type, id] = locality.split('|');
                const optionType = (type.slice(0, -1) + 'Id') as
                    | 'arrondissementId'
                    | 'provinceId'
                    | 'communeId';

                const call = await getAllLocalities(label.split(' (')[0]);
                if (!call) continue;

                const category = call.find((e) => e.code === type && e.options);
                if (!category) continue;

                const match = category.options.find(
                    (e: IReferenceLocalities) => e[optionType] === +id
                );

                if (match) {
                    localities.push(match);

                    const postalCodes = Array.isArray(match.codePostal)
                        ? match.codePostal.map(Number)
                        : [Number(match.codePostal)];

                    cps = cps.concat(postalCodes);
                }
            }

            return { localities, cps };
        },
        [getAllLocalities]
    );

    const setTypeOfferChildren = useCallback(
        (e: IReferenceSousContrat[]): IDropDownValueOfferTypeChildren[] => {
            return e.map((b) => ({
                value: b.code,
                key: b.code,
                title: b.libelle,
            }));
        },
        []
    );

    const getOffersDomains = useCallback(async () => {
        const data = await ReferenceService.getAllDomains();

        const mapSubDomains = (
            subDomains: IReferenceSousDomaine[]
        ): IDropDownValueChildren[] => {
            if (!subDomains?.length) return [];
            return subDomains.map((sd: IReferenceSousDomaine) => ({
                id: sd.id.toString(),
                value: sd.name,
                title: sd.label,
                key: sd.name,
            }));
        };

        const domains: IDropDownValue[] = data.map((e: IReferenceDomaine) => ({
            value: e.name,
            title: e.label,
            children: mapSubDomains(e.subDomain),
        }));

        setDomains(domains);
    }, []);

    const getSectors = useCallback(async () => {
        //const timeService = await AboutTimeService.aboutTime('offers');
        // if (timeService === 404) {
        const data = await ReferenceService.getSectors();
        const mapped = data.map((e) => ({
            value: e.code,
            title: e.name,
        }));
        setSectors(mapped);
        // } else {
        //     setIsModalOpen(true);
        //     setIsLoading(false);
        // }
    }, []);

    const getOffersTypes = useCallback(async () => {
        //const timeService = await AboutTimeService.aboutTime('offers');
        // if (timeService === 200) {
        const data = await ReferenceService.getAllOffersType();
        if (data && data.length) {
            const offersTypeData: IDropDownValueOfferType[] = data.map((e) => ({
                value: e.code,
                key: e.code,
                title: e.libelle,
                children: setTypeOfferChildren(e.sousCodeContrat),
                subOfferType: setTypeOfferChildren(e.sousCodeContrat),
            }));
            setOffersType(offersTypeData);
        }
        // } else {
        //     setIsModalOpen(true);
        //     setIsLoading(false);
        // }
    }, [setTypeOfferChildren]);

    const localisationFromObjectToQueryParams = useCallback(
        (localities: IReferenceLocalities[]): string => {
            return localities.reduce(
                (all: string, curr: IReferenceLocalities) => {
                    let typeCity = '';
                    let cityId = 0;
                    let cityLibelle = curr.libelle;
                    if (curr.provinceId) {
                        typeCity = 'provinces';
                        cityId = curr.provinceId;
                    } else if (curr.communeId) {
                        typeCity = 'communes';
                        cityId = curr.communeId;
                        cityLibelle += ` (${Array.isArray(curr.codePostal) ? curr.codePostal.join(', ') : curr.codePostal})`;
                    } else if (curr.arrondissementId && !curr.communeId) {
                        typeCity = 'arrondissements';
                        cityId = curr.arrondissementId;
                    }
                    return `${all}&localisations=${cityLibelle}|${typeCity}|${cityId}`;
                },
                ''
            );
        },
        []
    );

    const setFilterInQueryParams = useCallback(
        (paramsNotFromFilter?: ParamsNotFromFilter): void => {
            const localisation =
                '?localisation=' + offersFilterParams.offreFilter.localisation;
            const rawKeywords =
                offersFilterParams.offreFilter.texte ??
                offersFilterParams.offreFilter.references ??
                '';
            const keywordsString = Array.isArray(rawKeywords)
                ? rawKeywords.join(',')
                : rawKeywords;
            const keywords =
                keywordsString !== ''
                    ? '&keywords=' + encodeURIComponent(keywordsString)
                    : '';

            let domains = offersFilterParams.offreFilter.domainesImt
                ? offersFilterParams.offreFilter.domainesImt.join(',')
                : '';
            domains = domains !== '' ? '&domains=' + domains : '';

            let cleanedCodesContrat =
                offersFilterParams.offreFilter.codesContrat || [];
            cleanedCodesContrat = cleanedCodesContrat.filter(
                (code) => !code.startsWith('parent-')
            );
            const codesContrat = cleanedCodesContrat.length
                ? '&codesContrat=' + cleanedCodesContrat.join(',')
                : '';
            const regimesTravailRaw =
                offersFilterParams.offreFilter.regimesTravail ?? '';
            const regimesTravailString = Array.isArray(regimesTravailRaw)
                ? regimesTravailRaw.join(',') // choose your separator
                : regimesTravailRaw;
            const regimesTravail =
                regimesTravailString !== ''
                    ? '&regimesTravail=' + regimesTravailString
                    : '';

            let dateDerniereModification =
                offersFilterParams.offreFilter.dateDerniereModification || '';
            dateDerniereModification =
                dateDerniereModification !== ''
                    ? '&dateDerniereModification=' +
                      new Date(dateDerniereModification)
                          .toISOString()
                          .split('T')[0]
                    : '';
            const keywordSearchType =
                '&keywordSearchType=' +
                    offersFilterParams.offreFilter.keywordSearchType ||
                '&keywordSearchType=Partout';
            let langue = offersFilterParams.offreFilter.langue || '';
            langue = langue !== '' ? '&langue=' + langue : '';
            const isOffreActiris = offersFilterParams.offreFilter.isOffreActiris
                ? '&isOffreActiris=true'
                : '';
            const isOffreVdabForem = offersFilterParams.offreFilter
                .isOffreVdabForem
                ? '&isOffreVdabForem=true'
                : '';
            const isOfferPartner = offersFilterParams.offreFilter.isOfferPartner
                ? '&isOfferPartner=true'
                : '';
            const isOffreHandicap = offersFilterParams.offreFilter
                .isOffreHandicap
                ? '&isOffreHandicap=true'
                : '';
            let sector = offersFilterParams.offreFilter.secteursPanorama
                ? offersFilterParams.offreFilter.secteursPanorama.join(',')
                : '';
            sector = sector !== '' ? '&secteursPanorama=' + sector : '';
            const page = '&page=' + (offersFilterParams.pageOption.page || 1);
            let codesPostal = '';
            if (paramsNotFromFilter && paramsNotFromFilter.localities) {
                codesPostal = localisationFromObjectToQueryParams(
                    paramsNotFromFilter.localities
                );
            }
            window.history.replaceState(
                null,
                '',
                localisation +
                    keywords +
                    page +
                    domains +
                    sector +
                    regimesTravail +
                    codesContrat +
                    keywordSearchType +
                    codesPostal +
                    dateDerniereModification +
                    langue +
                    isOffreActiris +
                    isOffreVdabForem +
                    isOfferPartner +
                    isOffreHandicap
            );
            const query = queryParamsToState();
            setQueryParams(query);
        },
        [
            localisationFromObjectToQueryParams,
            queryParamsToState,
            offersFilterParams.offreFilter.localisation,
            offersFilterParams.offreFilter.texte,
            offersFilterParams.offreFilter.references,
            offersFilterParams.offreFilter.domainesImt,
            offersFilterParams.offreFilter.codesContrat,
            offersFilterParams.offreFilter.regimesTravail,
            offersFilterParams.offreFilter.dateDerniereModification,
            offersFilterParams.offreFilter.keywordSearchType,
            offersFilterParams.offreFilter.langue,
            offersFilterParams.offreFilter.isOffreActiris,
            offersFilterParams.offreFilter.isOffreVdabForem,
            offersFilterParams.offreFilter.isOfferPartner,
            offersFilterParams.offreFilter.isOffreHandicap,
            offersFilterParams.offreFilter.secteursPanorama,
            offersFilterParams.pageOption.page,
        ]
    );

    const setMinHeight = useCallback((): string => {
        const filterContainer = document.querySelector('.filter-search');
        if (!filterContainer) return '0px';
        return filterContainer.clientHeight + 60 + 'px';
    }, []);

    const getOffers = useCallback(
        async (
            filterParams = offersFilterParams,
            isFilter = false,
            loadedCount = offersLoadedCount,
            moreParams?: ParamsNotFromFilter
        ) => {
            if (cancellationTokenRef.current) {
                cancellationTokenRef.current.abort();
            }

            cancellationTokenRef.current = new AbortController();
            const params = structuredClone(filterParams);

            if (isFilter) {
                params.pageOption.page = 1;
                params.pageOption.from = 0;
            }

            try {
                const offers = await OfferService.getAllOffers(
                    params,
                    cancellationTokenRef.current.signal
                );

                setOffersList(offers);
                setOffersLoadedCount(
                    loadedCount + (offers.offers ? offers.offers.length : 0)
                );
                setIsLoading(false);
                setMinHeightState(!isMobile ? setMinHeight() : '0px');
                setFilterInQueryParams(
                    moreParams || { localities: selectedLocalities }
                );
                return offers;
            } catch (e) {
                if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                    throw e;
                }
                return;
            }
        },
        [
            offersFilterParams,
            offersLoadedCount,
            isMobile,
            selectedLocalities,
            setMinHeight,
            setFilterInQueryParams,
        ]
    );

    const getKeywordAndDomainFilter = useCallback(
        (keywords: string | null, launchSearch = true) => {
            setOffersFilterParams((prev) => {
                const newParams = structuredClone(prev);

                newParams.offreFilter.texte =
                    keywords && isNaN(Number(keywords)) ? String(keywords) : '';
                newParams.offreFilter.references =
                    keywords && !isNaN(Number(keywords)) ? [keywords] : [];
                newParams.pageOption.page = 1;

                if (props.selectedDomains) {
                    newParams.offreFilter.domainesImt =
                        props.selectedDomains.split(',');
                }

                if (launchSearch) {
                    void getOffers(newParams, true, 0);
                }
                return newParams;
            });
        },
        [props.selectedDomains, getOffers]
    );

    const fetchMoreData = useCallback(
        (page: number) => {
            setOffersFilterParams((prev) => {
                const newParams = structuredClone(prev);
                newParams.pageOption.page = page;
                newParams.pageOption.from =
                    (page - 1) * newParams.pageOption.pageSize;

                setOffersFilterParams(newParams);

                getOffers(newParams, false, 0)
                    .then(() => {
                        document.body.scrollTo({ top: 0, behavior: 'smooth' });
                    })
                    .catch((err) => console.error('GetOffers error:', err));

                return newParams;
            });
        },
        [getOffers]
    );

    const scrollToTop = useCallback(() => {
        const filtersTop = document.querySelector('#filterSearch');
        if (filtersTop) {
            filtersTop.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    const setStickyButton = useCallback(() => {
        const stickyContainer =
            document.querySelector<HTMLDivElement>('.list-container');
        const filters = document.querySelector<HTMLDivElement>('#filterSearch');
        const stickyButtonContainer = document.querySelector<HTMLDivElement>(
            '.scroll-top-container-list'
        );

        if (!stickyContainer || !filters || !stickyButtonContainer) return;

        const stickyContainerBottom: number =
            stickyContainer.offsetTop +
            (stickyContainer.offsetHeight - filters.offsetHeight);

        if (
            filters.getBoundingClientRect().bottom < 0 &&
            window.pageYOffset < stickyContainerBottom
        ) {
            if (stickyButtonContainer.classList.contains('bottom')) {
                stickyButtonContainer.classList.remove('bottom');
            } else {
                stickyButtonContainer.style.width =
                    stickyContainer.offsetWidth - 30 + 'px';
                stickyButtonContainer.classList.add('sticky');
            }
        } else if (window.pageYOffset >= stickyContainerBottom) {
            stickyButtonContainer.classList.remove('sticky');
            stickyButtonContainer.classList.add('bottom');
        } else {
            if (stickyButtonContainer.classList.contains('sticky')) {
                stickyButtonContainer.classList.remove('sticky');
            }
        }
    }, []);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const resizeStickyContainer = useCallback(() => {
        const stickyButtonContainer = document.querySelector<HTMLDivElement>(
            '.scroll-top-container-list'
        );
        const stickyContainer =
            document.querySelector<HTMLDivElement>('.list-container');

        if (
            stickyButtonContainer &&
            stickyContainer &&
            stickyButtonContainer.classList.contains('sticky')
        ) {
            stickyButtonContainer.style.width =
                stickyContainer.offsetWidth - 30 + 'px';
        }
    }, []);

    useEffect(() => {
        void localityDb.open();

        const queryParams = queryParamsToState();
        const localisations = findInQuery(queryParams, 'localisations');

        if (localisations) {
            const array = Array.isArray(localisations)
                ? localisations
                : [localisations];

            void localitiesQueryTolocalitiresObj(array).then((e) => {
                setOffersFilterParams((prev) => ({
                    ...prev,
                    offreFilter: {
                        ...prev.offreFilter,
                        codesPostal: e.cps,
                    },
                }));

                setSelectedLocalities(e.localities);

                window.history.replaceState(
                    null,
                    '',
                    window.location.search +
                        localisationFromObjectToQueryParams(e.localities)
                );
            });
        }
        void (async () => {
            if (
                sessionStorage.currentOfferList &&
                sessionStorage.currentOfferListPage &&
                sessionStorage.currentOfferId
            ) {
                const storedPage = Number.parseInt(
                    sessionStorage.currentOfferListPage,
                    10
                );

                setOffersList(JSON.parse(sessionStorage.currentOfferList));

                setOffersFilterParams((prev) => ({
                    ...prev,
                    pageOption: {
                        ...prev.pageOption,
                        page:
                            Number.isFinite(storedPage) && storedPage > 0
                                ? storedPage
                                : 1,
                    },
                }));
            } else {
                await getOffersDomains();
                await getOffersTypes();
                await getSectors();
                await getOffers(offersFilterParams, false, 0);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isMobile) return;

        const update = () => {
            const h = setMinHeight();
            if (h !== minHeight) setMinHeightState(h);
        };

        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [isMobile, minHeight, setMinHeight]);

    useEffect(() => {
        const stickyButtonContainer = document.querySelector(
            '.scroll-top-container-list'
        );
        const stickyContainer = document.querySelector('.list-container');
        const offerId = sessionStorage.currentOfferId;

        if (
            offerId &&
            sessionStorage.currentOfferList &&
            sessionStorage.currentOfferListPage
        ) {
            const isFromOfferOrLogin =
                document.referrer.includes(props.offerDetailPageName) ||
                document.referrer.includes(props.connectionPageUrl);

            if (isFromOfferOrLogin) {
                document.getElementById('currentOffer')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }

            sessionStorage.removeItem('currentOfferId');
            sessionStorage.removeItem('currentOfferList');
            sessionStorage.removeItem('currentOfferListPage');
        }

        if (
            stickyButtonContainer instanceof HTMLElement &&
            stickyContainer instanceof HTMLElement
        ) {
            setStickyButton();
            stickyButtonContainer.style.visibility = 'visible';
        }

        const handleScroll = () => setStickyButton();
        const handleResize = () => resizeStickyContainer();

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [
        props.offerDetailPageName,
        props.connectionPageUrl,
        isMobile,
        setMinHeight,
        setStickyButton,
        resizeStickyContainer,
    ]);

    useEffect(() => {
        const all = document.querySelectorAll('*');
        all.forEach((el) => {
            el.addEventListener('scroll', () => {});
        });
    }, []);

    const offersCount = offersList ? offersList.total.toString() : '';
    const hasTitle = props.title || props.shortDescription;

    const modalStyle = {
        content: {
            maxWidth: '600px',
            height: 'fit-content',
            margin: 'auto',
            paddingBottom: '24px',
        },
    };

    const searchKeyword =
        offersFilterParams.offreFilter.texte ||
        (offersFilterParams.offreFilter.references?.[0] ?? '');
    return (
        <>
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel=""
                style={modalStyle}
            >
                <div className="container" onClick={closeModal}>
                    <div className="row">
                        <div className="col-12">
                            <h2 className="m-3">
                                <i className="icon-warning"></i>Error occured
                            </h2>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-10">
                            <h3 className="m-0">An error has occured</h3>
                            <p>
                                The Api didn&#39;t respond while fetching the
                                information, try later...
                            </p>
                        </div>
                        <div className="col text-center mt-4">
                            <i
                                className="icon-sad"
                                style={{ fontSize: '250%' }}
                            ></i>
                        </div>
                    </div>
                </div>
            </Modal>
            <div
                className="banner banner--full-width"
                style={{
                    backgroundImage: 'url(' + props.imageUrl + ')',
                    backgroundPosition: props.bgPos,
                }}
            >
                <div className="container">
                    {hasTitle && !isCovid && (
                        <div className="banner--full-width__title">
                            <h1>
                                {props.title}
                                {!props.sectorTitle || !props.jobTitle || (
                                    <span>{props.keywords}</span>
                                )}
                                <span>{props.shortDescription}</span>
                            </h1>
                        </div>
                    )}
                    <div className={isCovid ? 'd-none' : 'd-block d-lg-none'}>
                        <SearchOffersKeywordsWidget
                            keywordInputId={'mobileKeywordInput'}
                            dictWorkFamilies={props.dictWorkFamilies}
                            domainSelectId={'mobileDomainSelect'}
                            offerSearchUrl={null}
                            dropdownValueDomains={domains}
                            getKeywordAndDomainFilter={
                                getKeywordAndDomainFilter
                            }
                            selectedDomains={selectedDomains}
                            keywords={
                                offersFilterParams.offreFilter.texte ||
                                (offersFilterParams.offreFilter.references
                                    ? offersFilterParams.offreFilter
                                          .references[0]
                                    : '')
                            }
                            dictNbSelectedDomain={props.dictNbSelectedDomain}
                            dictSearchBy={props.dictSearchBy}
                            dictJobsSkillsKeywords={
                                props.dictJobsSkillsKeywords
                            }
                            dictSelectedDomain={props.dictSelectedDomain}
                            dictDomain={props.dictDomain}
                            dictSearch={props.dictSearch}
                            searchParamsTypeActiris={isEmployerActirisOnly}
                            dictRemoveFilter={props.dictRemoveFilter}
                            isCovid={isCovid}
                            setCurrentSearchedWord={setCurrentSearchedWord}
                        ></SearchOffersKeywordsWidget>
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="row" style={{ minHeight: minHeight }}>
                    {!isCovid && (
                        <OfferSearchFilter
                            offersFilterParams={offersFilterParams}
                            domains={domains}
                            getDomains={getOffersDomains}
                            sectors={sectors}
                            getSectors={getSectors}
                            getOffersType={getOffersTypes}
                            refineResults={props.dictRefineResults}
                            dropdownValueDomains={domains}
                            queryParams={queryParams}
                            selectedLocalities={selectedLocalities}
                            offerTypeFilter={offersType}
                            sectorFilter={sectors}
                            offerCreateDate={props.dictOfferCreateDate}
                            location={props.dictLocation}
                            workTime={props.dictWorkTime}
                            language={props.dictLanguage}
                            level={props.dictLevel}
                            getOffers={(...args) => {
                                void getOffers(...args);
                            }}
                            all={props.dictAll}
                            day={props.dictDay}
                            week={props.dictWeek}
                            month={props.dictMonth}
                            belgium={props.dictBelgium}
                            foreign={props.dictForeign}
                            fullTime={props.dictFullTime}
                            partialTime={props.dictPartialTime}
                            isSjabloons={props.isSjabloons}
                            with={props.dictWith}
                            without={props.dictWithout}
                            withConvention={props.dictWithConvention}
                            offerSourceTitle={props.dictOfferSourceTitle}
                            offerSourceEmployment={
                                props.dictOfferSourceEmployment
                            }
                            offerSourceStudent={props.dictOfferSourceStudent}
                            dictSeeJobSeekerOffers={
                                props.dictSeeJobSeekerOffers
                            }
                            dictRemoveFilter={props.dictRemoveFilter}
                            dictIsCovidOnly={props.dictIsCovidOnly}
                            dictFilterShowOnly={props.dictFilterShowOnly}
                            showCovidOffers={props.showCovidOffers}
                            oneLevel={props.dictOneLevel}
                            localityList={getAllLocalities}
                            domainSelectId={'mobileDomainSelect'}
                            dictAllOffers={props.dictAllOffers}
                            dictLast30Days={props.dictLast30Days}
                            dictLast7Days={props.dictLast7Days}
                            dictTypeOfferStage={props.dictTypeOfferStage}
                            dictTypeOfferStudent={props.dictTypeOfferStudent}
                            dictSelectedDomain={props.dictSelectedDomain}
                            dictWorkFamily={props.dictWorkFamily}
                            dictSector={props.dictSector}
                            dictWorkFamilies={props.dictWorkFamilies}
                            dictKeywordTitleOnly={props.dictKeywordTitleOnly}
                            dictActirisOffers={props.dictActirisOffers}
                            dictVdabOffers={props.dictVdabOffers}
                            dictOthersOffers={props.dictOthersOffers}
                            dictNbSelectedDomain={props.dictNbSelectedDomain}
                            dictSearch={props.dictSearch}
                            searchParamsTypeActiris={isEmployerActirisOnly}
                            dictLocalitiesDefaultMessage={
                                props.dictLocalitiesDefaultMessage
                            }
                            dictHandicapOffers={props.dictHandicapOffers}
                            dictLocations={props.dictLocations}
                            dictAdvertDescription={props.dictAdvertDescription}
                            dictAdvertLabel={props.dictAdvertLabel}
                            dictAdvertTitle={props.dictAdvertTitle}
                            dictNbSelectedDomains={props.dictNbSelectedDomains}
                            dictTempHospitalOffers={
                                props.dictTempHospitalOffers
                            }
                            dictLocalities={props.dictLocalities}
                            dictLanguageInformation={
                                props.dictLanguageInformation
                            }
                            dictLanguageMoreInformation={
                                props.dictLanguageMoreInformation
                            }
                            dictOnFocus={props.dictOnFocus}
                            dictZoneBelgium={props.dictZoneBelgium}
                            alertUrl={props.alertUrl}
                            alertTitle={props.alertTitle}
                            alertDescription={props.alertDescription}
                            alertEnabled={alertEnabled}
                            disableResultFilter={
                                offersList && offersList.total
                                    ? offersList.total === 0
                                    : true
                            }
                        ></OfferSearchFilter>
                    )}
                    <div
                        className={
                            'list-container col-12' +
                            (isCovid
                                ? ' col-lg-10 offset-lg-1 full-width'
                                : ' col-lg-8')
                        }
                    >
                        <div
                            className={
                                'bloc-search' +
                                (isCovid ? ' d-block' : ' d-none d-lg-block')
                            }
                        >
                            {hasTitle && isCovid && (
                                <h1>
                                    {props.title}{' '}
                                    <span>
                                        {!props.sectorTitle ||
                                            !props.jobTitle || (
                                                <>{props.keywords}</>
                                            )}
                                        {props.shortDescription}
                                    </span>
                                </h1>
                            )}
                            <SearchOffersKeywordsWidget
                                keywordInputId={'desktopKeywordInput'}
                                domainSelectId={'desktopDomainSelect'}
                                offerSearchUrl={null}
                                dropdownValueDomains={domains}
                                dictWorkFamilies={props.dictWorkFamilies}
                                getKeywordAndDomainFilter={
                                    getKeywordAndDomainFilter
                                }
                                selectedDomains={selectedDomains}
                                keywords={
                                    offersFilterParams.offreFilter.texte ||
                                    (offersFilterParams.offreFilter.references
                                        ? offersFilterParams.offreFilter
                                              .references[0]
                                        : '')
                                }
                                dictNbSelectedDomain={
                                    props.dictNbSelectedDomain
                                }
                                dictSearchBy={props.dictSearchBy}
                                dictJobsSkillsKeywords={
                                    props.dictJobsSkillsKeywords
                                }
                                dictSelectedDomain={props.dictSelectedDomain}
                                dictDomain={props.dictDomain}
                                dictSearch={props.dictSearch}
                                searchParamsTypeActiris={isEmployerActirisOnly}
                                dictRemoveFilter={props.dictRemoveFilter}
                                isCovid={isCovid}
                                setCurrentSearchedWord={setCurrentSearchedWord}
                            ></SearchOffersKeywordsWidget>
                        </div>
                        {props.showCovidOffers &&
                            props.showCovidOffers.toUpperCase() === 'TRUE' && (
                                <div className="bloc-results__intro">
                                    <p>{props.dictCovidDescription}</p>
                                </div>
                            )}
                        {props.descriptionBeforeOffers && (
                            <div className="bloc-results__intro">
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: props.descriptionBeforeOffers,
                                    }}
                                />
                            </div>
                        )}

                        {props.sectorTitle &&
                            props.sectorDescription &&
                            isSectorFilter && (
                                <div className="bloc-results__intro">
                                    <h4>
                                        {props.sectorTitle
                                            .replace(
                                                regexNameLandingPageSeo,
                                                props.keywords
                                            )
                                            .replace(
                                                regexCountLandingPageSeo,
                                                offersCount
                                            )}
                                    </h4>
                                    <p>
                                        {props.sectorDescription
                                            .replace(
                                                regexNameLandingPageSeo,
                                                props.keywords
                                            )
                                            .replace(
                                                regexCountLandingPageSeo,
                                                offersCount
                                            )}
                                    </p>
                                </div>
                            )}

                        {props.jobTitle &&
                            props.jobDescription &&
                            !isSectorFilter && (
                                <div className="bloc-results__intro">
                                    <h4>
                                        {props.jobTitle
                                            .replace(
                                                regexNameLandingPageSeo,
                                                props.keywords
                                            )
                                            .replace(
                                                regexCountLandingPageSeo,
                                                offersCount
                                            )}
                                    </h4>
                                    <p>
                                        {props.jobDescription
                                            .replace(
                                                regexNameLandingPageSeo,
                                                props.keywords
                                            )
                                            .replace(
                                                regexCountLandingPageSeo,
                                                offersCount
                                            )}
                                    </p>
                                </div>
                            )}
                        <OfferList
                            searchKeyword={searchKeyword}
                            currentSearchedWord={currentSearchedWord}
                            connectionPageUrl={props.connectionPageUrl}
                            isAuthenticated={props.isAuthenticated}
                            keywords={
                                offersFilterParams.offreFilter.texte ||
                                (offersFilterParams.offreFilter.references
                                    ? offersFilterParams.offreFilter
                                          .references[0]
                                    : '')
                            }
                            dashboardPageUrl={props.dashboardPageUrl}
                            offersList={offersList}
                            offersListPage={offersFilterParams.pageOption.page}
                            hasMore={hasMore}
                            language={props.language}
                            offerDetailPageName={props.offerDetailPageName}
                            homepageUrl={props.homepageUrl}
                            dictOffers={props.dictOffers}
                            dictThe={props.dictThe}
                            dictContractType={props.dictContractType}
                            dictPreselectActiris={props.dictPreselectActiris}
                            dictBelgium={props.dictBelgium}
                            dictForeign={props.dictForeign}
                            dictOfferSave={props.dictOfferSave}
                            dictOfferDelete={props.dictOfferDelete}
                            dictBasketOffers={props.dictBasketOffers}
                            fetchMoreData={fetchMoreData}
                            isLoading={isLoading}
                            dictNoResultTitle={props.dictNoResultTitle}
                            dictNoResultDescription={
                                props.dictNoResultDescription
                            }
                            dictSelectExplanation={props.dictSelectExplanation}
                            dictResultSearch={props.dictResultSearch}
                            dictFor={props.dictFor}
                            dictToday={props.dictToday}
                            dictEdited={props.dictEdited}
                            dictPublished={props.dictPublished}
                            dictYesterday={props.dictYesterday}
                            dictDaysAgo={props.dictDaysAgo}
                            dictWeeksAgo={props.dictWeeksAgo}
                            dictWeekAgo={props.dictWeekAgo}
                            dictMonthsAgo={props.dictMonthsAgo}
                            dictMonthAgo={props.dictMonthAgo}
                            dictYearsAgo={props.dictYearsAgo}
                            dictYearAgo={props.dictYearAgo}
                            dictSelectActirisDescription={
                                props.dictSelectActirisDescription
                            }
                            dictOffer={props.dictOffer}
                            dictRef={props.dictRef}
                        ></OfferList>
                        <div className="scroll-top-container-list">
                            <a
                                onClick={(e) => {
                                    e.preventDefault();
                                    scrollToTop();
                                }}
                                className="btn-secondary"
                                href="#"
                            >
                                <span className="sr-only">scrollToTop</span>
                                <i className="icon-angleTop mr-0"></i>
                            </a>
                        </div>

                        {offersList && offersList.offers.length > 0 && (
                            <Paginator
                                page={offersFilterParams.pageOption.page}
                                pageSize={10}
                                totalItem={offersList.total}
                                pageChange={fetchMoreData}
                            ></Paginator>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

WidgetHelper.register(SearchOffersWidget, 'search-offers-widget');
